"use client";

import { useId, useRef, type ComponentPropsWithoutRef, type ElementType, type KeyboardEvent, type ReactNode } from "react";

import { cx } from "../../lib/cx";
import { Icon, type IconName } from "../core/Icon";
import s from "./Tabs.module.css";

export interface TabItem {
  value: string;
  label: string;
  icon?: IconName;
  count?: number;
  /** `warning` pinta el contador en ámbar mientras la pestaña no está activa: algo espera ahí. */
  countTone?: "warning";
  /** Con `href` la pestaña es un enlace (una ruta), no un estado. */
  href?: string;
}

export interface TabsProps extends Omit<ComponentPropsWithoutRef<"div">, "onChange"> {
  tabs?: TabItem[];
  value?: string;
  onChange?: (value: string) => void;
  /** Componente para las pestañas con `href` (p. ej. `Link` de Next). Default `"a"`. */
  as?: ElementType;
  /** Prefijo de los ids de pestañas y paneles; pasale el mismo a `TabPanel`. */
  id?: string;
}

/** Barra de pestañas subrayada. Flechas, Home y End mueven el foco y activan. */
export function Tabs({ tabs = [], value, onChange, as, id, className, ...rest }: TabsProps) {
  const generado = useId();
  const base = id ?? generado;
  const lista = useRef<HTMLDivElement>(null);
  const LinkTag: ElementType = as ?? "a";

  const alTeclear = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.value === value);
    let destino = -1;
    if (e.key === "ArrowRight") destino = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") destino = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") destino = 0;
    else if (e.key === "End") destino = tabs.length - 1;
    if (destino < 0 || !tabs[destino]) return;
    e.preventDefault();
    const tab = tabs[destino];
    onChange?.(tab.value);
    const el = lista.current?.querySelector<HTMLElement>(`[data-value="${tab.value}"]`);
    el?.focus();
    // Una pestaña que es ruta navega al enfocarse con teclado igual que con click.
    if (tab.href) el?.click();
  };

  return (
    <div ref={lista} role="tablist" className={cx(s.lista, className)} onKeyDown={alTeclear} {...rest}>
      {tabs.map((t) => {
        const activa = t.value === value;
        const contenido = (
          <>
            {t.icon ? <Icon name={t.icon} size={16} /> : null}
            {t.label}
            {t.count !== undefined ? (
              <span className={s.contador} data-tone={t.countTone}>
                {t.count}
              </span>
            ) : null}
          </>
        );
        if (t.href) {
          return (
            <LinkTag
              key={t.value}
              href={t.href}
              role="tab"
              aria-selected={activa}
              aria-current={activa ? "page" : undefined}
              tabIndex={activa ? 0 : -1}
              data-value={t.value}
              className={s.tab}
            >
              {contenido}
            </LinkTag>
          );
        }
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            id={`${base}-tab-${t.value}`}
            aria-selected={activa}
            aria-controls={`${base}-panel-${t.value}`}
            tabIndex={activa ? 0 : -1}
            data-value={t.value}
            className={s.tab}
            onClick={() => onChange?.(t.value)}
          >
            {contenido}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps extends ComponentPropsWithoutRef<"div"> {
  value: string;
  /** Un panel inactivo no se renderiza. */
  activo: boolean;
  /** El mismo `id` que se le pasó a `Tabs`, para enlazar `aria-labelledby`. */
  tabsId?: string;
  children?: ReactNode;
}

/** El contenido de una pestaña. Con `tabsId` queda enlazado a su pestaña. */
export function TabPanel({ value, activo, tabsId, children, className, ...rest }: TabPanelProps) {
  if (!activo) return null;
  return (
    <div
      role="tabpanel"
      id={tabsId ? `${tabsId}-panel-${value}` : undefined}
      aria-labelledby={tabsId ? `${tabsId}-tab-${value}` : undefined}
      tabIndex={0}
      className={cx(s.panel, className)}
      {...rest}
    >
      {children}
    </div>
  );
}
